import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  DateInput,
  Edit,
  EditButton,
  Filter,
  maxLength,
  minLength,
  minValue,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  required,
  SearchInput,
  SelectInput,
  Show,
  SingleFieldList,
  TabbedShowLayout,
  TextField,
  TextInput,
  useGetOne,
  useTranslate,
} from 'react-admin';
import Typography from '@material-ui/core/Typography';
import { useMediaQuery } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { withStyles } from '@material-ui/core/styles';
import {
  ZippityDatagrid,
  ZippitySimpleForm,
  ZippityTab,
} from '../../components';
import CarFaxChipField from '../carfax-record/CarFaxChipField';
import { usStateOptions } from '../../UsStates';
import { printCustomer } from '../customer';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import ResponsiveAside from '../../components/ResponsiveAside';
import ReferenceChild from '../../components/ReferenceChild';
import { perPage } from '../../helper';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const styles = {
  asidePadding: {
    padding: '10px 0 10px 15px',
  },
};

const stopPropagation = (e) => e.stopPropagation();

const CustomerVehicleFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <TextInput source="year" alwaysOn />
    <TextInput source="make" alwaysOn />
    <TextInput source="model" alwaysOn />
    <TextInput source="vehicle_id" />
    <ReferenceInput reference="customer" source="customer_id" perPage={perPage}>
      <ReferenceChild
        {...props}
        optionText={(choice) => printCustomer(choice)}
        source="customer_id"
      />
    </ReferenceInput>
  </Filter>
);

export const CustomerVehicleList = (props) => (
  <ListWithCSVImport
    {...props}
    filters={<CustomerVehicleFilter />}
    bulkActionButtons={<BulkDeleteButton />}
    sort={{ field: 'year', order: 'DESC' }}
    perPage={50}
  >
    <ZippityDatagrid rowClick="show">
      <TextField source="created_at" />
      <TextField source="modified_at" />
      <TextField source="year" />
      <TextField source="make" />
      <TextField source="model" />
      <TextField source="color" />
      <TextField source="trim" />
      <TextField source="engine_name" />
      <TextField source="vin" />
      <TextField source="license_plate_number" />
      <TextField source="license_plate_state" />
      <ReferenceField
        source="customer.default_client_location_id"
        reference="client-location"
        allowEmpty
      >
        <TextField source="short_name" />
      </ReferenceField>
      <ReferenceField
        source="customer.customer_id"
        reference="customer"
        link="show"
        allowEmpty
      >
        <TextField source="email" />
      </ReferenceField>
      <BooleanField source="is_deleted" />
      <EditButton />
    </ZippityDatagrid>
  </ListWithCSVImport>
);

const RepairOrders = ({ customerId }) => {
  const { loaded, data } = useGetOne('customer', customerId);
  if (!loaded) {
    return 'loading...';
  }
  if (!data || !data.sws) {
    return '';
  }
  return data.sws.map(({ id, date }) => (
    <span key={id}>
      <Link onClick={stopPropagation} href={`/#/scheduled-work/${id}/show`}>
        {date}
      </Link>
      <br />
    </span>
  ));
};

const Aside = withStyles(styles)(({ record, classes }) => {
  const translate = useTranslate();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  return (
    <ResponsiveAside>
      <div className={isSmall ? `${classes.asidePadding}` : null}>
        {record && record.motor_image_api_url && (
          <Link href={record.motor_image_api_url}>
            <img src={record.motor_image_url} alt="vehicle" />
          </Link>
        )}
        <hr className="vehicle-hr" />
        <Typography variant="h6">
          {translate('resources.vehicle.tabs.scheduled_works')}
        </Typography>
        {record && record.customer && record.customer.id && (
          <Typography variant="body2">
            <RepairOrders customerId={record.customer.id} />
          </Typography>
        )}
        <hr className="vehicle-hr" />
        <Typography variant="h6">
          {translate('resources.vehicle.tabs.customer')}
        </Typography>
        {record && record.customer && (
          <Typography variant="body2">
            <Link
              onClick={stopPropagation}
              href={`/#/customer/${record.customer.id}/show`}
            >
              {record.customer.first_name} {record.customer.last_name}
            </Link>
            <br />
          </Typography>
        )}
        <hr className="vehicle-hr" />
        <Typography variant="h6">
          {translate('resources.vehicle.tabs.motor')}
        </Typography>
        {record && (
          <Typography variant="body2">
            <Link
              onClick={stopPropagation}
              href={record.motor_oil_spec_api_url}
            >
              {translate('resources.vehicle.fields.motor_oil_spec_api_url')}
            </Link>
            <br />
            <Link
              onClick={stopPropagation}
              href={record.motor_tire_spec_api_url}
            >
              {translate('resources.vehicle.fields.motor_tire_spec_api_url')}
            </Link>
          </Typography>
        )}
        {record && record.is_deleted && (
          <div>
            <hr className="vehicle-hr" />
            <Typography color="secondary" variant="h6">
              {translate('resources.vehicle.fields.is_deleted_value')}
            </Typography>
          </div>
        )}
        <BooleanField source="is_deleted" />
      </div>
    </ResponsiveAside>
  );
});

export const CustomerVehicleShow = (props) => (
  <Show
    aside={<Aside />}
    title={<CustomTitle title="resources.vehicle.title" />}
    {...props}
  >
    <TabbedShowLayout>
      <ZippityTab label="resources.vehicle.tabs.basics">
        <TextField source="created_at" />
        <TextField source="year" />
        <TextField source="make" />
        <TextField source="model" />
        <TextField source="color" />
        <TextField source="trim" />
        <TextField source="engine_name" />
        <TextField source="oil_capacities" />
        <TextField source="oil_viscosities" />
        <TextField source="standard_tire_sizes" />
        <TextField source="vin" />
        <TextField source="license_plate_state" />
        <TextField source="license_plate_number" />
        <TextField source="license_plate_expiration" />
        <TextField source="inspection_expiration" />
      </ZippityTab>
      <ZippityTab label="resources.vehicle.tabs.third_party_data">
        <TextField source="bed_type_lengths" />
        <TextField source="body_style_types" />
        <TextField source="wheel_base_lengths" />

        <TextField source="pt_front_tire_size" />
        <TextField source="pt_rear_tire_size" />
        <TextField source="pt_base_msrp" />
        <TextField source="pt_wheel_base_shortest_inches" />
        <TextField source="pt_wheel_base_longest_inches" />
        <TextField source="pt_segmentation_description" />
        <TextField source="pt_vehicle_type_description" />
        <TextField source="pt_front_tire_pressure_lbs" />
        <TextField source="pt_rear_tire_pressure_lbs" />
        <ReferenceManyField
          label="CarFax Records"
          target="vin"
          source="vin"
          reference="carfax-record"
          perPage={100}
        >
          <SingleFieldList linkType="show">
            <CarFaxChipField />
          </SingleFieldList>
        </ReferenceManyField>
      </ZippityTab>
    </TabbedShowLayout>
  </Show>
);

const validateRequiredId = [
  required(),
  minValue(1000000000, 'Please enter a valid customer ID'),
];
export const validateRequiredYear = [
  required(),
  minValue(1900, 'Please enter a valid 4 digit year'),
];
export const validateRequiredString = [required(), minLength(1), maxLength(63)];

export const CustomerVehicleEdit = (props) => (
  <Edit
    undoable={false}
    title={<CustomTitle title="resources.vehicle.title" />}
    aside={<Aside />}
    {...props}
  >
    <ZippitySimpleForm redirect="show">
      <NumberInput validate={validateRequiredYear} source="year" />
      <TextInput validate={validateRequiredString} source="make" />
      <TextInput validate={validateRequiredString} source="model" />
      <TextInput validate={validateRequiredString} source="color" />
      <TextInput source="trim" />
      <TextInput source="engine_name" />
      <TextInput source="oil_capacities" />
      <TextInput source="oil_viscosities" />
      <TextInput source="standard_tire_sizes" />
      <TextInput source="vin" />
      <SelectInput source="license_plate_state" choices={usStateOptions} />
      <TextInput source="license_plate_number" />
      <DateInput source="license_plate_expiration" />
      <DateInput source="inspection_expiration" />
      {/* TODO: change this to month instead of date - possibly @material-ui DatePicker */}
      {/* https://material-ui-pickers.dev/demo/datepicker */}
      <BooleanInput source="is_deleted" />
    </ZippitySimpleForm>
  </Edit>
);

export const CustomerVehicleCreate = (props) => {
  const { location } = props;
  const { state } = location;
  if (state) {
    return (
      <Create {...props}>
        <ZippitySimpleForm redirect="show">
          {/* Fields below are from the record passed through the customer page redirect */}
          {/* The 4 fields below are all pulled from data in the customer record */}
          <TextField source="first_name" />
          <TextField source="last_name" />
          <TextField source="email" />
          <TextField source="customer_id" />
          <NumberInput validate={validateRequiredYear} source="year" />
          <TextInput validate={validateRequiredString} source="make" />
          <TextInput validate={validateRequiredString} source="model" />
          <TextInput validate={validateRequiredString} source="color" />
          <TextInput source="trim" />
          <TextInput source="engine_name" />
          <TextInput source="vin" />
          <SelectInput source="license_plate_state" choices={usStateOptions} />
          <TextInput source="license_plate_number" />
        </ZippitySimpleForm>
      </Create>
    );
  }
  return (
    <Create {...props}>
      <ZippitySimpleForm redirect="show">
        <NumberInput validate={validateRequiredId} source="customer_id" />
        <NumberInput validate={validateRequiredYear} source="year" />
        <TextInput validate={validateRequiredString} source="make" />
        <TextInput validate={validateRequiredString} source="model" />
        <TextInput validate={validateRequiredString} source="color" />
        <TextInput source="trim" />
        <TextInput source="engine_name" />
        <TextInput source="vin" />
        <TextInput source="license_plate_state" />
        <TextInput source="license_plate_number" />
      </ZippitySimpleForm>
    </Create>
  );
};
